import { useState } from 'react';
import { api, mediaUrl, uploadMedia } from '../lib/api';
import { useAction, useApi } from '../lib/useApi';
import { useAuth } from '../lib/auth';
import { Empty, ErrorBox, Field, Loading, Notice, PageHeader, LoadFailed , FormActions, useDraft } from '../components/ui';
import { LicenceTag, LICENCE_OPTIONS, SensitivityTag } from '../components/Provenance';

interface Person {
  id: string;
  display_name: string;
  relation_label: string | null;
  child_profile_id: string | null;
}
interface Keepsake {
  id: string;
  kind: string;
  title: string;
  caption: string | null;
  media_asset_id: string;
  taken_year: number | null;
  taken_month: number | null;
  precision: string;
  place: string | null;
  transcript: string | null;
  licence: string;
  sensitivity: string;
  people: { id: string; name: string; note: string | null }[];
}

const KINDS = [
  { key: 'photo', label: 'Photograph', icon: '🖼️' },
  { key: 'letter', label: 'Letter', icon: '✉️' },
  { key: 'certificate', label: 'Certificate', icon: '📜' },
  { key: 'document', label: 'Document', icon: '📄' },
  { key: 'other', label: 'Something else', icon: '📎' },
];

function when(k: Keepsake): string {
  if (!k.taken_year) return 'Date unknown';
  if (k.precision === 'decade') return `${Math.floor(k.taken_year / 10) * 10}s`;
  if (!k.taken_month) return String(k.taken_year);
  return `${new Date(2000, k.taken_month - 1, 1).toLocaleString(undefined, { month: 'long' })} ${k.taken_year}`;
}

/**
 * The family archive.
 *
 * A photograph with nobody named in it is lost within two generations. The
 * naming is the whole feature — everything else here exists to make naming
 * people easy enough that somebody actually does it.
 */
export function ArchivePage() {
  const { user } = useAuth();
  const { data, error, loading, reload } = useApi<{ keepsakes: Keepsake[] }>('/keepsakes');
  const tree = useApi<{ people: Person[] }>('/tree');
  const act = useAction();
  const [filter, setFilter] = useState('');

  const canWrite = user?.role === 'parent' || user?.role === 'grandparent' || user?.role === 'teen';
  const people = (tree.data?.people ?? []).filter((p) => !p.child_profile_id);

  if (loading) return <Loading />;
  if (error || !data) return <LoadFailed error={error} onRetry={reload} />;
  const items = filter ? data.keepsakes.filter((k) => k.kind === filter) : data.keepsakes;

  return (
    <>
      <PageHeader
        kicker="Preserve"
        title="Family archive"
        lead="Photographs, letters and certificates — with the people in them named, while somebody still knows who they are."
      />
      <Notice text={act.notice} />
      <ErrorBox error={act.error} />

      <div className="tabs" role="tablist" aria-label="Filter by kind">
        <button role="tab" aria-selected={filter === ''} onClick={() => setFilter('')}>
          All
        </button>
        {KINDS.map((k) => (
          <button key={k.key} role="tab" aria-selected={filter === k.key} onClick={() => setFilter(k.key)}>
            {k.icon} {k.label}
          </button>
        ))}
      </div>

      <div className="grid grid-2">
        <section className="stack" aria-label="Keepsakes">
          {items.length === 0 && (
            <Empty>
              Nothing here yet. Start with one photograph and name everyone in it — that naming is the part that disappears first.
            </Empty>
          )}
          {items.map((k) => (
            <article key={k.id} className="card stack">
              <div className="spread">
                <h3 style={{ margin: 0 }}>
                  <span aria-hidden="true">{KINDS.find((x) => x.key === k.kind)?.icon ?? '📎'}</span> {k.title}
                </h3>
                <span className="muted small">{when(k)}</span>
              </div>
              <KeepsakeImage mediaAssetId={k.media_asset_id} alt={k.title} />
              {k.caption && <p className="small">{k.caption}</p>}
              {k.place && <p className="muted small yo">{k.place}</p>}
              <div className="row" style={{ gap: 6 }}>
                <SensitivityTag sensitivity={k.sensitivity} />
                <LicenceTag licence={k.licence} />
              </div>
              <NamePeople keepsake={k} people={people} canWrite={canWrite} onChange={reload} />
              {k.transcript && (
                <details>
                  <summary className="small">What it says</summary>
                  <p className="small yo" style={{ whiteSpace: 'pre-wrap' }}>
                    {k.transcript}
                  </p>
                </details>
              )}
            </article>
          ))}
        </section>

        {canWrite && <AddKeepsake people={people} onAdded={reload} />}
      </div>
    </>
  );
}

/** Fetched on demand: the download link is private and short-lived. */
function KeepsakeImage({ mediaAssetId, alt }: { mediaAssetId: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  if (failed) return <p className="muted small">That file would not open. It is still safely stored.</p>;
  if (!url) {
    return (
      <div>
        <button
          className="btn small ghost"
          onClick={async () => {
            try {
              setUrl(await mediaUrl(mediaAssetId));
            } catch {
              setFailed(true);
            }
          }}
        >
          Show it
        </button>
      </div>
    );
  }
  return <img className="keepsake-img" src={url} alt={alt} onError={() => setFailed(true)} />;
}

function NamePeople({ keepsake, people, canWrite, onChange }: { keepsake: Keepsake; people: Person[]; canWrite: boolean; onChange: () => void }) {
  const act = useAction();
  const [adding, setAdding] = useState('');
  const named = new Set(keepsake.people.map((p) => p.id));

  return (
    <div className="stack tight">
      <span className="label">Who is in it</span>
      {keepsake.people.length === 0 ? (
        <span className="muted small">Nobody named yet — this is the part that gets lost.</span>
      ) : (
        <div className="row" style={{ gap: 6 }}>
          {keepsake.people.map((p) => (
            <span key={p.id} className="chip yo">
              {p.name}
              {canWrite && (
                <button
                  className="chip-x"
                  aria-label={`Remove ${p.name}`}
                  onClick={async () => {
                    await act.run(() => api(`/keepsakes/${keepsake.id}/people/${p.id}`, { method: 'DELETE' }));
                    onChange();
                  }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {canWrite && people.some((p) => !named.has(p.id)) && (
        <div className="row">
          <select aria-label="Name someone in this" value={adding} onChange={(e) => setAdding(e.target.value)}>
            <option value="">Name someone…</option>
            {people
              .filter((p) => !named.has(p.id))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.relation_label ? `${p.relation_label} ${p.display_name}` : p.display_name}
                </option>
              ))}
          </select>
          <button
            className="btn small ghost"
            disabled={!adding || act.busy}
            onClick={async () => {
              await act.run(() => api(`/keepsakes/${keepsake.id}/people`, { body: { personId: adding } }), 'Named.');
              setAdding('');
              onChange();
            }}
          >
            Add
          </button>
        </div>
      )}
      <ErrorBox error={act.error} />
    </div>
  );
}

function AddKeepsake({ people, onAdded }: { people: Person[]; onAdded: () => void }) {
  const act = useAction();
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm, clearDraft] = useDraft('keepsake', {
    kind: 'photo',
    title: '',
    caption: '',
    year: '',
    place: '',
    transcript: '',
    licence: 'family_only',
    sensitivity: 'ordinary',
  });
  const [chosen, setChosen] = useState<string[]>([]);

  return (
    <form
      className="card stack"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!file) return act.setError('Choose a photograph or a document first.');
        const ok = await act.run(async () => {
          const mediaAssetId = await uploadMedia(file, 'photo');
          return api('/keepsakes', {
            body: {
              kind: form.kind,
              title: form.title,
              caption: form.caption || undefined,
              mediaAssetId,
              personIds: chosen,
              year: form.year ? Number(form.year) : undefined,
              precision: form.year ? 'year' : 'unknown',
              place: form.place || undefined,
              transcript: form.transcript || undefined,
              licence: form.licence,
              sensitivity: form.sensitivity,
            },
          });
        }, 'Kept. Name anyone else in it whenever you like.');
        if (ok) {
          setForm({ ...form, title: '', caption: '', year: '', place: '', transcript: '' });
          setFile(null);
          setChosen([]);
          onAdded();
        }
      }}
    >
      <h2>Add to the archive</h2>
      <Field id="kk-file" label="The photograph or document" hint="A phone photograph of an old print works perfectly well.">
        <input id="kk-file" type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </Field>
      <Field id="kk-title" label="What is it">
        <input id="kk-title" className="yo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={160} />
      </Field>
      <div className="row">
        <select aria-label="Kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          {KINDS.map((k) => (
            <option key={k.key} value={k.key}>
              {k.icon} {k.label}
            </option>
          ))}
        </select>
        <input aria-label="Year" placeholder="Year (if known)" inputMode="numeric" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
        <input aria-label="Place" className="yo" placeholder="Place" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} />
      </div>
      <Field id="kk-people" label="Who is in it" hint="The single most valuable thing you can add. Pick everyone you can name.">
        <div className="row" style={{ gap: 6 }}>
          {people.map((p) => (
            <label key={p.id} className="check">
              <input
                type="checkbox"
                checked={chosen.includes(p.id)}
                onChange={(e) => setChosen(e.target.checked ? [...chosen, p.id] : chosen.filter((x) => x !== p.id))}
              />
              <span className="yo">{p.display_name}</span>
            </label>
          ))}
          {people.length === 0 && <span className="muted small">Add people to the Voice Tree first.</span>}
        </div>
      </Field>
      <Field id="kk-caption" label="Anything you remember" hint="Optional — but the thing your grandchildren will read.">
        <textarea id="kk-caption" rows={2} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} maxLength={2000} />
      </Field>
      <Field id="kk-transcript" label="If it has writing on it, type it out" hint="Optional.">
        <textarea id="kk-transcript" rows={2} className="yo" value={form.transcript} onChange={(e) => setForm({ ...form, transcript: e.target.value })} />
      </Field>
      <div className="row">
        <label className="stack tight" style={{ flex: '1 1 12rem' }}>
          <span className="label">Who may see this</span>
          <select value={form.licence} onChange={(e) => setForm({ ...form, licence: e.target.value })}>
            {LICENCE_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="stack tight" style={{ flex: '1 1 12rem' }}>
          <span className="label">Is it restricted?</span>
          <select
            value={form.sensitivity}
            onChange={(e) => {
              const sensitivity = e.target.value;
              const licence = sensitivity === 'restricted' && !['family_only', 'extended_family'].includes(form.licence) ? 'family_only' : form.licence;
              setForm({ ...form, sensitivity, licence });
            }}
          >
            <option value="ordinary">No — anyone in the family</option>
            <option value="adults_only">Adults only</option>
            <option value="restricted">Restricted</option>
          </select>
        </label>
      </div>
      <ErrorBox error={act.error} />
      <FormActions submitLabel="Keep it" busy={act.busy} onClear={clearDraft} />
    </form>
  );
}
